import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import {
  REMINDER_IS_NOT_DELIVERY_NOTE,
  remindRefusal,
  remindRefusalOf,
  remindWaitSeconds,
  type RemindRefusalDetail,
} from '@/lib/docusign';
import { formatDateTime } from '@/lib/format';
import type { DocuSignEnvelope } from '@/lib/types';
import { Button } from '@/components/ui';

/*
 * "Resend for signature" — asking DocuSign to email the request again.
 *
 * ONE component, and it is written to have two mount points from the start: the
 * quote's `EnvelopePanel`, and the Send/Resend/Change-signer menu on the quotes
 * LIST. Modelled deliberately on `ResendApprovalControl`, because everything hard
 * here is the same shape and it is all **state per record**: an in-flight guard,
 * an abort on unmount, a live cooldown countdown, and an outcome that must not
 * overclaim. Two copies would agree the day they were written and drift on the
 * first correction — and what would drift is a sentence about whether an email
 * went to a customer.
 *
 * ── ⚠️ THIS IS NOT THE BACKGROUND POLLER, AND THEY SIT ON ONE PANEL ──────
 *
 * The poller READS status on a ~5 minute floor and cannot send anything. This
 * EMAILS A CUSTOMER, only when somebody presses it. **Never render a cadence
 * beside this button** — "we check every five minutes" next to a Resend control
 * reads as "we remind every five minutes", which would be a promise to mail a
 * customer on a timer. See the section on both in `@/lib/docusign`.
 *
 * ── ⚠️ THE ENVELOPE IS UNTOUCHED ─────────────────────────────────────────
 *
 * Resend here means REMIND: the signing link already in the customer's inbox
 * stays valid, any partial signature is kept, and the quote's stage does not
 * move. It is not void-and-recreate under a friendlier name — that is "Change
 * signer and resend", which is a different, destructive operation.
 *
 * ── ⚠️ A `200` IS NOT A DELIVERY ─────────────────────────────────────────
 *
 * There is no webhook and no receipt. The success line says DocuSign accepted the
 * request and explicitly does NOT say the customer was reached — see
 * `REMINDER_IS_NOT_DELIVERY_NOTE`. Reporting the verb pressed rather than the
 * outcome recorded is the mistake this repo has made three times on Xero.
 */

/** Where this is mounted, which decides how much room the feedback gets. */
type Variant = 'panel' | 'menu';

interface RemindState {
  sending: boolean;
  /** Ours, on success. Deliberately never says "delivered". */
  sent: string | null;
  /** A classified refusal — the server's own sentence rides inside it. */
  refusal: RemindRefusalDetail | null;
  /** Anything unclassified: a 403, a network failure, a code newer than the map. */
  error: string | null;
  /**
   * The cooldown countdown, in seconds. `null` when there is nothing to count —
   * which is the ordinary case, including on a `429` whose `details` carried
   * nothing usable. A caller then reads the server's sentence and no clock.
   */
  waitSeconds: number | null;
}

const IDLE: RemindState = {
  sending: false,
  sent: null,
  refusal: null,
  error: null,
  waitSeconds: null,
};

export interface RemindEnvelopeControlProps {
  envelope: DocuSignEnvelope;
  /**
   * `ability.canUpdate('quotes')` from the call site — **never `user.role`, never
   * `/permissions/me`**. `POST /docusign/envelopes/:id/remind` is `WRITE_ROLES`,
   * the same floor as `/refresh`, because it emails a customer.
   */
  canManage: boolean;
  /** The true reason a write is refused. Rendered as text as well as a `title`. */
  manageDenial?: string | null;
  /**
   * The envelope as the server sees it afterwards, so a parent can replace its
   * copy rather than re-fetching. `lastRemindedAt` has moved on it; nothing else
   * has.
   */
  onReminded?: (envelope: DocuSignEnvelope) => void;
  variant?: Variant;
  /**
   * Something else on the row is busy. Kept separate from this control's own
   * `sending` so a status check does not read as a reminder in flight.
   */
  busy?: boolean;
}

/** `3:07`, or `48s` under a minute. A bare second count over ten minutes is unreadable. */
function countdownLabel(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes}:${String(rest).padStart(2, '0')}`;
}

export function RemindEnvelopeControl({
  envelope,
  canManage,
  manageDenial,
  onReminded,
  variant = 'panel',
  busy = false,
}: RemindEnvelopeControlProps) {
  const [state, setState] = useState<RemindState>(IDLE);

  /*
   * The in-flight controller, so an unmount aborts rather than resolving into a
   * component that is gone. `sending` in state drives the UI; this ref is what
   * teardown can reach.
   */
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => () => abortRef.current?.abort(), []);

  /*
   * Reset when the row changes identity or the envelope moves on.
   *
   * ⚠️ **Adjusted DURING RENDER, not in an effect** — React's own documented
   * pattern for resetting state on a prop change. An effect would paint the
   * previous envelope's outcome for a frame first, and on a list of rows
   * recycling as pages change that frame is visible: one signer's "reminder
   * queued" under another signer's name.
   *
   * `status` is in the identity because a reminder outcome under an envelope that
   * has since completed is a true sentence about a past event rendered as though
   * it were the current state.
   */
  const identity = `${envelope.id}:${envelope.status}`;
  const [renderedFor, setRenderedFor] = useState(identity);
  if (renderedFor !== identity) {
    setRenderedFor(identity);
    setState(IDLE);
  }

  /*
   * The countdown: one timer, replaced on each tick, cleared on unmount. It stops
   * AT zero rather than nulling afterwards — the guard below is `> 0`, so zero
   * already reads as "the wait is over" and re-enables the button.
   */
  useEffect(() => {
    if (state.waitSeconds === null || state.waitSeconds <= 0) return;
    const timer = window.setTimeout(() => {
      setState((prev) =>
        prev.waitSeconds === null
          ? prev
          : { ...prev, waitSeconds: Math.max(0, prev.waitSeconds - 1) },
      );
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [state.waitSeconds]);

  /*
   * ⚠️ **Asked BEFORE the click, and it is not the same question as the
   * cooldown.** `remindRefusal` answers the two permanent cases — the envelope is
   * finished, or it was delivered as a link and DocuSign never emailed anybody —
   * from data already in hand. The cooldown is invisible from here: no company or
   * envelope read carries `lastRemindedAt` into this app, so a just-reminded
   * envelope LOOKS remindable and the `429` is where anybody finds out. That is
   * the accepted contract, exactly as it is on the company-approval resend.
   */
  const preRefusal = remindRefusal(envelope);

  const send = useCallback(async () => {
    // Checked here as well as expressed through `loading`: the disabled button is
    // the optimisation, this is the guarantee. Each press emails a customer, so a
    // double-fire is a real cost rather than a wasted call.
    if (state.sending) return;

    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;

    setState({ ...IDLE, sending: true });

    try {
      const response = await api.remindDocusignEnvelope(envelope.id, controller.signal);
      if (controller.signal.aborted) return;
      /*
       * ⚠️ Worded from what was actually observed. DocuSign accepted a request;
       * nothing here can know whether mail arrived, and the response says nothing
       * about it either — `lastRemindedAt` moves whether or not anything landed.
       */
      setState({ ...IDLE, sent: REMINDER_IS_NOT_DELIVERY_NOTE });
      onReminded?.(response.data);
    } catch (caught) {
      if (controller.signal.aborted) return;

      if (caught instanceof ApiError) {
        /*
         * 401 is left entirely to `api.ts`, which attempts one refresh and then
         * ends the session; a message here would contradict it mid sign-out.
         */
        if (caught.status === 401) return;

        /*
         * ⚠️ A `403` is handled even though the control is gated. A role can be
         * taken away under an access token that lasts over five days, so the gate
         * is the optimisation and this is the guarantee. It carries **no
         * `details`**, so it is a plain sentence and never a field error.
         */
        if (caught.status === 403) {
          setState({
            ...IDLE,
            error:
              manageDenial ??
              'Your account no longer has permission to send this reminder.',
          });
          return;
        }

        const refusal = remindRefusalOf(caught);
        if (refusal) {
          setState({ ...IDLE, refusal, waitSeconds: remindWaitSeconds(refusal.cooldown) });
          return;
        }
      }

      /*
       * Unrecognised: the server's own sentence, never a classification we
       * invented. An unknown code means "newer than this branch", not "not a
       * refusal" — the degradation `quoteRefusalOf` and `resendRefusalOf` both
       * specify.
       */
      setState({
        ...IDLE,
        error:
          caught instanceof Error
            ? caught.message
            : 'The reminder could not be sent. Try again.',
      });
    } finally {
      /*
       * A backstop, and a no-op today: every branch above settles `sending` as
       * part of the state it writes, and every early return happens only when the
       * signal is aborted, which this guard skips. It is here so a branch added
       * later cannot leave the button spinning for ever.
       */
      if (!controller.signal.aborted) {
        setState((prev) => (prev.sending ? { ...prev, sending: false } : prev));
      }
      if (abortRef.current === controller) abortRef.current = null;
    }
  }, [envelope.id, manageDenial, onReminded, state.sending]);

  const waiting = state.waitSeconds !== null && state.waitSeconds > 0;
  const terminalRefusal = state.refusal?.terminal === true;

  /*
   * Why the button is off, in the order the reasons are tested. A bare `disabled`
   * makes a permission denial, a finished envelope, an embedded delivery and an
   * ordinary cooldown all identical, so every one of these carries its own
   * sentence.
   */
  const denial = canManage
    ? null
    : (manageDenial ?? 'You do not have permission to send this reminder.');
  const preRefusalReason =
    preRefusal === 'terminal'
      ? `This envelope is ${envelope.status}, so there is nobody left to remind. Send a new request if the quote still needs signing.`
      : preRefusal === 'embedded'
        ? 'This one was delivered as a signing link, so DocuSign never emailed the signer and has nothing to re-send. Hand over a fresh link instead.'
        : null;

  const disabledReason =
    denial ?? preRefusalReason ?? (terminalRefusal ? (state.refusal?.message ?? null) : null);
  const disabled = disabledReason !== null || waiting || busy;

  /*
   * ⚠️ **Both a countdown and the server's own sentence, never one or the
   * other.** They are true at once and they say different things — the sentence
   * names when DocuSign was last asked, the clock says how long is left — so a
   * `??` between them would drop one. That is the silent-drop rule this repo
   * records four instances of.
   */
  const cooldownLine =
    state.refusal?.kind === 'too-recently'
      ? [
          state.refusal.message,
          waiting ? `Another can be sent in ${countdownLabel(state.waitSeconds ?? 0)}.` : null,
          state.refusal.cooldown?.lastRemindedAt
            ? `Last asked at ${formatDateTime(state.refusal.cooldown.lastRemindedAt)}.`
            : null,
        ]
          .filter((line): line is string => line !== null)
          .join(' ')
      : null;

  const label = 'Resend for signature';
  const title = disabledReason ?? label;

  const feedback =
    cooldownLine ??
    state.error ??
    (state.refusal && state.refusal.kind !== 'too-recently' ? state.refusal.message : null) ??
    state.sent;

  const feedbackTone =
    state.sent !== null && cooldownLine === null && state.error === null && state.refusal === null
      ? 'text-fg-subtle'
      : 'text-danger';

  if (variant === 'menu') {
    /*
     * ⚠️ **CORRECTED before the first mount.** *SUPERSEDED: "In a menu there is
     * no room for a sentence, so the reason goes on the `title` … and into an
     * off-screen live region."* The `title` and the live region STAY; what was
     * wrong was making them the only channel. **A disabled reason is not helper
     * text** — it is on the standing exception list precisely because a bare
     * `disabled` makes a permission denial, a finished envelope, an embedded
     * delivery and an ordinary cooldown identical — and a disabled button has
     * `pointer-events: none`, so its tooltip never reaches the person it was
     * written for.
     *
     * ⚠️ And the case it hides is the MAJORITY one: `embedded` is the delivery
     * the send dialog opens on, and DocuSign emails nobody on an embedded
     * envelope, so there is nothing to re-send. Left title-only, most rows would
     * show a dead item explaining nothing.
     *
     * A menu has vertical room a table cell does not, which is what makes this
     * different from the no-prose-in-a-cell rule the superseded sentence was
     * reaching for. FEEDBACK still goes to the `title` and the live region alone
     * — that arrives after a press, on a control the pointer can still reach.
     *
     * ⚠️ **`role="menuitem"` is not decoration.** A `role="menu"` may only
     * contain menu items, and the list page's menu finds its focusable children
     * with a `[role="menuitem"]` query — so a bare `<Button>` here would be
     * skipped by arrow-key navigation and by the Tab wrap, leaving the middle
     * item of three reachable by pointer alone. `ghost` and the full-width start
     * alignment match the items it sits between; a primary button in a list of
     * ghost rows reads as a different kind of thing.
     */
    return (
      <>
        <Button
          role="menuitem"
          variant="ghost"
          size="sm"
          className="w-full justify-start"
          loading={state.sending}
          disabled={disabled}
          title={feedback ?? title}
          onClick={() => void send()}
        >
          {waiting ? `${label} (${countdownLabel(state.waitSeconds ?? 0)})` : label}
        </Button>
        {/*
          ⚠️ Rendered only while NOT counting down: the countdown is already in
          the label, and repeating it underneath says the same thing twice while
          pushing the items apart. `disabledReason` never holds the cooldown
          anyway — the cooldown is invisible from here until a `429`.
        */}
        {disabledReason && !waiting && (
          <p className="m-0 px-2.5 pb-1 text-2xs text-fg-subtle [overflow-wrap:anywhere]">
            {disabledReason}
          </p>
        )}
        <span role="status" className="sr-only">
          {feedback ?? ''}
        </span>
      </>
    );
  }

  return (
    <>
      <Button
        size="sm"
        loading={state.sending}
        disabled={disabled}
        title={title}
        onClick={() => void send()}
      >
        {waiting ? `${label} (${countdownLabel(state.waitSeconds ?? 0)})` : label}
      </Button>
      {/*
        The reason a disabled button is disabled, as TEXT and not only a `title` —
        a disabled button has `pointer-events: none`, so its tooltip never reaches
        the person it was written for. Rendered beside whatever feedback also
        holds, because a denial and a refusal are different facts.
      */}
      {disabledReason && !waiting && (
        <p className="m-0 w-full text-2xs text-fg-subtle [overflow-wrap:anywhere]">
          {disabledReason}
        </p>
      )}
      {feedback && (
        <p
          role="status"
          className={`m-0 w-full text-2xs [overflow-wrap:anywhere] ${feedbackTone}`}
        >
          {feedback}
        </p>
      )}
    </>
  );
}
